import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { useTranslation } from "react-i18next";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import {
  FaEye,
  FaFileImport,
  FaFileDownload,
  FaFileUpload,
  FaFolderOpen,
  FaHistory,
  FaPen,
  FaProjectDiagram,
  FaPlus,
  FaRegFile,
  FaRegFileAlt,
  FaTrashAlt,
  FaUnlink,
} from "react-icons/fa";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import U2000YesNoDialog from "../../../../../u2000Core/components/dialog/U2000YesNoDialog";
import type { useU2000LUStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000LUHandlers";
import { useLocation, useNavigate } from "react-router-dom";
import { isValidId } from "../../../../../u2000Core/tools/U2000IdUtils";
import { useU2000DeleteHandlers } from "../../../../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000HandleApiError } from "../../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../../u2000Core/context/toast/U2000ToastContext";
import type { U3141MasterLotChainFormExtendedHandlers } from "../../../U3141MasterLotChainForm";
import { NS, U3141ROLES } from "../../../../U3141";
import {
  doDeleteChildLotChainData,
  luHandleAppiError,
  U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG,
  type U3141ChildLotChain,
  type U3141DocumentTypeEntry,
  type U3141LotDetailDocument,
  type U3141LotDetailOperationTypeFromInitLU,
  type U3141LandRegisterLocationState,
  type U3141MasterLotChain,
  type U3141NodePositions,
} from "../../../../U3141Service";
import { useU3141Context } from "../../../../U3141Context";
import { useU3141MasterChildLotChainHandler } from "./U3141MasterChildLotChainHandlers";
import { useU3141LotChainGraphApiHandlers } from "./U3141UseLotChainGraphApiHandlers";
import { useU3141AnnotatedLotChainNotification } from "./U3141UseAnnotatedLotChainNotification";
import { useU3141ReconstituteChainFromPdfNotification } from "../U3141UseReconstituteChainFromPdfNotification";
import { useU3141HistoriqueCadastralNotification } from "../U3141UseHistoriqueCadastralNotification";
import { useU3141ImportChainAnnotationsNotification } from "../useU3141ImportChainAnnotationsNotification";
import { useU3141DeleteChildLotChainFromGraphHandlers } from "./U3141UseDeleteChildLotChainFromGraphHandlers";
import { useU3141GraphConcordanceHandlers } from "./U3141UseGraphConcordanceHandlers";
import { useU3141OpenDocumentPdfFileHandlers } from "../../u3141ChildLotChain/U3141UseOpenDocumentPdfFileHandlers";
import { u3141IsNasTruthDocumentType } from "../../../../tools/u3141OpenDocumentPdfFile";
import U31414MasterChildLotChainDeleteDlg from "../../../common/U3141MasterChildLotChainDeleteDlg";
import { getOperationTypeLabel } from "../../../common/U3141LotOperationType";
import { U2000StringArrayDisplay } from "../../../../../u2000Core/components/tables/display/U2000StringArrayDisplay";
import { U3141LotStateEnum } from "../../../common/U3141LotStateType";
import U2000ReactFlowDialog, {
  type U2000ReactFlowContextMenuItem,
  type U2000ReactFlowEdgeContextMenuItem,
  type U2000ReactFlowPaneContextMenuItem,
  type U2000ReactFlowPaneContextOpenDetail,
} from "../../../../../u2000Core/components/dialog/U2000ReactFlowDialog";
import { U3141AddLotFromGraphDialog } from "./U3141AddLotFromGraphDialog";
import { U3141EditLotFromGraphDialog } from "./U3141EditLotFromGraphDialog";
import type { Edge, Node } from "@xyflow/react";
import type { U3141GraphConcordanceEdgeData } from "../../../../tools/u3141GraphConcordanceHandles";
import { sortByConcordanceOrder } from "../../u3141ChildLotChain/U3141ConcordanceUtils";
import { U3141PdfAnnotationEditorDialog } from "../../u3141ChildLotChain/U3141PdfAnnotationEditorDialog";
import { u3141IsGraphDeletableChildLotChain } from "../../../../tools/u3141IsGraphDeletableChildLotChain";
import { u3141IsRequestedLotChildLotChain } from "../../../../tools/u3141IsRequestedLotChildLotChain";
import { u3141MasterLotChainHistoriqueActionState } from "../../../../tools/u3141MasterLotChainHistoriqueActionState";
import { u3141IsOrphanChildLotChain } from "../../../../tools/u3141IsOrphanChildLotChain";
import { U3141GraphChildLotDocumentsDialog } from "./U3141GraphChildLotDocumentsDialog";
import type { U3141ChildLotChainDocumentsChangeDetail } from "../../u3141ChildLotChain/U3141ChildLotChainDocumentsPanel";
import { u3141SyncChildLotChainDocumentTypesFromDocumentsList } from "../../../../tools/u3141SyncChildLotChainDocumentTypesFromDocumentsList";
import { u3141SyncChildLotChainGridFromLotChainGraph } from "../../../../tools/u3141SyncChildLotChainGridFromLotChainGraph";
import { u3141AppendGraphContextMenuSection } from "../../../../tools/u3141AppendGraphContextMenuSection";
import { u3141GraphContextMenuDocumentLabel } from "../../../../tools/u3141GraphContextMenuDocumentLabel";
import { u3141LotHasAnnotatedLotChainDocument } from "../../../../tools/u3141LotHasAnnotatedLotChainDocument";
import { U3141AddDocumentDialog } from "../../u3141ChildLotChain/U3141AddDocumentDialog";
import { useU3141GetDocumentsHandlers } from "../../u3141ChildLotChain/U3141UseDocumentsHandlers";
import {
  u3141MapDocumentTypeEntriesToExistingDocuments,
  u3141ResolveImportDocumentMenuTypes,
} from "../../../../tools/u3141DocumentAddDialogTypes";

/**
 * Ordre d’affichage dans le menu contextuel du graphe (clic droit sur un lot) :
 * Tous les lots : générer chaîne annotée (READ) avant le bloc documents.
 * Lot demandé : section **Historique cadastral** (récupérer, télécharger HTML, import PDF) — retirée de la toolbar formulaire.
 * Bloc chaîne annotée : générer, import annotations (pas « Édition annotation »).
 * Bloc documents : lien PDF chaîne annotée en tête si présent, autres types, puis gérer les documents… (READ).
 * Puis Éditer (+ supprimer si éligible).
 */
const U3141_GRAPH_CONTEXT_MENU_DOCUMENT_ORDER: Record<string, number> = {
  rfLotHistoriqueCadastral: 10,
  rfLotDetailIndex: 20,
  rfLotDetailPlan: 30,
  rfLotPlansCorrectionCadastrale: 40,
  rfLotLivreRenvoi: 50,
  rfLotDetailAnnotatedLotChain: 5,
};

function u3141CompareDocumentEntriesForGraphContextMenu(a: U3141DocumentTypeEntry, b: U3141DocumentTypeEntry): number {
  const oa = U3141_GRAPH_CONTEXT_MENU_DOCUMENT_ORDER[a.documentType] ?? 900;
  const ob = U3141_GRAPH_CONTEXT_MENU_DOCUMENT_ORDER[b.documentType] ?? 900;
  if (oa !== ob) return oa - ob;
  const byName = (a.documentName ?? "").localeCompare(b.documentName ?? "", undefined, { sensitivity: "base" });
  if (byName !== 0) return byName;
  return String(a.id).localeCompare(String(b.id));
}

// Typage des props du composant
interface U3141MasterChildLotChainBodyProps {
  u3141MasterLotChainFormHandlers: U3141MasterLotChainFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction, language?: string, operationTypes?: U3141LotDetailOperationTypeFromInitLU[]): U2000TableColumn[] {
  const lang = language || "fr";
  return [
    U2000IdColumnDef,
    {
      name: "lotDetail.lot",
      label: t("lblLotDetail.lot", { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
    {
      name: "lotDetail.partieDeLot",
      label: t("lblLotDetail.partieDeLot", { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      widthClass: "w-[8%]",
      type: "string",
      align: "center",
      display: (_column: unknown, value: boolean | undefined) => (
        <span>{value === true ? t("lblYes", { ns: NS }) : value === false ? t("lblNo", { ns: NS }) : ""}</span>
      ),
    },
    {
      name: "lotDetail.district.propertyDistrict",
      label: t("lblLotDetail.propertyDistrict", { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      type: "string",
      align: "left",
    },
    {
      name: "lotDetail.cadastre.cadastre",
      label: t("lblLotDetail.cadastre", { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      type: "string",
      align: "left",
    },
    {
      name: "lotDetail.operationType",
      label: t("lblLotDetail.operationType", { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      type: "string",
      align: "left",
      display: (_column: any, value: any, _row: any) => {
        return <span>{getOperationTypeLabel(value ?? "", lang, operationTypes)}</span>;
      },
    },
    {
      name: "lotDetail.concordanceOrigines",
      label: t("lblLotDetail.concordanceOrigines", { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      type: "string",
      align: "left",
      display: (column: any, value: any, row: any) => {
        return <U2000StringArrayDisplay column={column} value={value} row={row} />;
      },
    },
    {
      name: "lotDetail.lotState",
      label: t("lblLotDetail.lotStateVerification", { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      type: "string",
      align: "left",
      display: (_column: any, value: any, _row: any) => {
        const getLabel = (val: string) => {
          const found = U3141LotStateEnum.find((type) => type.value === val);
          if (!found) return val;
          if (typeof found.label === "object") {
            const key = lang as keyof typeof found.label;
            return found.label[key] ?? found.label.fr;
          }
          return found.label;
        };
        return <span>{getLabel(value)}</span>;
      },
    },
  ];
}

// Composant principal pour la gestion des actions d'une unité logique
const U3141MasterChildLotChainBody: React.FC<U3141MasterChildLotChainBodyProps> = ({ u3141MasterLotChainFormHandlers }) => {
  // Translation
  const { t, i18n } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [isLotChainGraphLoading, setLotChainGraphLoading] = React.useState(false);
  /** Éditeur PDF (chaîne annotée) ouvert depuis le menu contextuel du graphe ; `childLotChain` sert au contexte concordance. */
  const [pdfAnnotationEditor, setPdfAnnotationEditor] = useState<{
    documentId: string;
    childLotChain: U3141ChildLotChain;
  } | null>(null);
  const [isGraphDeleteConfirmOpen, setGraphDeleteConfirmOpen] = useState(false);
  const [pendingGraphDeleteNode, setPendingGraphDeleteNode] = useState<Node | null>(null);
  const [isAddLotFromGraphOpen, setAddLotFromGraphOpen] = useState(false);
  const [addLotNodePosition, setAddLotNodePosition] = useState({ x: 0, y: 0 });
  const [isEditLotFromGraphOpen, setEditLotFromGraphOpen] = useState(false);
  const [editLotFromGraphChild, setEditLotFromGraphChild] = useState<U3141ChildLotChain | null>(null);
  const [graphManageDocumentsChild, setGraphManageDocumentsChild] = useState<U3141ChildLotChain | null>(null);
  const [graphAddDocument, setGraphAddDocument] = useState<{
    child: U3141ChildLotChain;
    initialDocumentType: string;
    existingDocuments: U3141LotDetailDocument[];
  } | null>(null);

  const { luStateHandlers, gridMasterChildLotChainStateHandlers, unitInitLU } = useU3141Context();

  const u3141MasterChildLotChainHandlers = useU3141MasterChildLotChainHandler({
    u2000AxiosAuthenticated,
    luStateHandlers: luStateHandlers as unknown as ReturnType<typeof useU2000LUStateHandlers>,
    gridStateHandlers: gridMasterChildLotChainStateHandlers as unknown as ReturnType<typeof useU2000GridStateHandlers<U3141ChildLotChain>>,
    t,
    navigate,
    organizationId: u3141MasterLotChainFormHandlers.getData?.()?.id ?? "",
    language: i18n?.language ?? "fr",
    lotDetailOperationTypes: unitInitLU.lotDetailOperationTypes,
  });

  // Transposer les données du formStateHandlers au gridStateHandlers pour LogicalUnitActions
  const columns = useMemo(() => getColumnsDef(t, i18n?.language, unitInitLU.lotDetailOperationTypes), [t, i18n?.language, unitInitLU.lotDetailOperationTypes]);

  useEffect(() => {
    const currentColumns = gridMasterChildLotChainStateHandlers.getColumns?.() ?? [];
    if (JSON.stringify(currentColumns) !== JSON.stringify(columns)) {
      gridMasterChildLotChainStateHandlers.setSize(9999);
      gridMasterChildLotChainStateHandlers.setColumns(columns);
      gridMasterChildLotChainStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
      gridMasterChildLotChainStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));
    }

    // Initialiser les données du tableau (tri par ordre concordance : parent avant enfant, ex. 23 puis 23-9 puis 23-9-1)
    const childLotChainsRaw = (u3141MasterLotChainFormHandlers.getData?.()?.childLotChains as U3141ChildLotChain[]) ?? [];
    const childLotChains = Array.isArray(childLotChainsRaw) ? sortByConcordanceOrder(childLotChainsRaw) : [];
    const currentData = gridMasterChildLotChainStateHandlers.getData?.() ?? [];
    if (JSON.stringify(currentData) !== JSON.stringify(childLotChains)) {
      gridMasterChildLotChainStateHandlers.setData(childLotChains);
      gridMasterChildLotChainStateHandlers.setTotalRows(childLotChains.length);
    }

    // Gérer les erreurs spécifiques au champ LogicalUnitActions
    const errorsField = u3141MasterLotChainFormHandlers.getOtherErrors()?.childLotChains;
    const normalizedErrors = errorsField !== undefined ? (Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []) : [];
    const currentErrors = gridMasterChildLotChainStateHandlers.getErrors?.() ?? [];
    if (JSON.stringify(currentErrors) !== JSON.stringify(normalizedErrors)) {
      gridMasterChildLotChainStateHandlers.setErrors(normalizedErrors);
    }
  }, [u3141MasterLotChainFormHandlers.getData(), t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u3141MasterChildLotChainHandlers.dataChangedEvent === 0) return;

    const childLotChains = gridMasterChildLotChainStateHandlers.getData();
    u3141MasterLotChainFormHandlers.setData({
      ...u3141MasterLotChainFormHandlers.getData?.(),
      childLotChains: childLotChains,
    });
  }, [u3141MasterChildLotChainHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridMasterChildLotChainStateHandlers.getSelectedRows();
    return u3141MasterLotChainFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT || selectedRows.length !== 1 ? true : false;
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableViewBtn(): boolean {
    return u3141MasterLotChainFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT;
  }

  // Gestion des Delete Record avec confirmation
  const applyChildLotChainDeletedFromGrid = useCallback(
    (deletedRows: U3141ChildLotChain[]) => {
      let newData = gridMasterChildLotChainStateHandlers.getData();
      let totalRows = gridMasterChildLotChainStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        const lotLabel = row.lotDetail?.lot?.trim() || row.id;
        toast.success(t("msgGraphRemoveLotFromChainSuccess", { lot: lotLabel, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });

      gridMasterChildLotChainStateHandlers.setData(newData);
      gridMasterChildLotChainStateHandlers.setTotalRows(totalRows);
      u3141MasterChildLotChainHandlers.setDataChangedEvent(u3141MasterChildLotChainHandlers.dataChangedEvent + 1);
    },
    [gridMasterChildLotChainStateHandlers, t, toast, u3141MasterChildLotChainHandlers],
  );

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridMasterChildLotChainStateHandlers ? gridMasterChildLotChainStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (childLotChainId, row) => {
      return await doDeleteChildLotChainData(u2000AxiosAuthenticated, u3141MasterLotChainFormHandlers.getData().id, childLotChainId, {
        id: row.id,
        version: row.version ?? 0,
      });
    },

    onSuccess: (deletedRows) => {
      applyChildLotChainDeletedFromGrid(deletedRows);
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const masterLotChainId = u3141MasterLotChainFormHandlers.getData?.()?.id ?? "";

  const graphAddDocumentChildId = graphAddDocument?.child.id ?? "";
  const { loadDocuments: loadGraphAddDocuments } = useU3141GetDocumentsHandlers({
    masterLotChainId,
    childLotChainId: graphAddDocumentChildId,
  });

  const graphAddDocumentAddableTypes = useMemo(
    () =>
      unitInitLU.addableDocumentTypes?.length > 0
        ? unitInitLU.addableDocumentTypes
        : [...U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG],
    [unitInitLU.addableDocumentTypes],
  );

  const graphAddDocumentSingleSlotTypes = useMemo(
    () =>
      unitInitLU.singleSlotDocumentTypes?.length > 0 ? unitInitLU.singleSlotDocumentTypes : ["rfLotDetailIndex"],
    [unitInitLU.singleSlotDocumentTypes],
  );

  const graphAddDocumentCurrentProject: U3141MasterLotChain | null = useMemo(() => {
    const child = graphAddDocument?.child;
    if (!child?.masterLotChain) {
      return null;
    }
    return {
      id: child.masterLotChain.id,
      projectNumber: child.masterLotChain.projectNumber ?? "",
      projectDescription: child.masterLotChain.projectDescription ?? "",
      requestedLot: "",
      createdAt: "",
      version: 0,
      childLotChains: [],
    };
  }, [graphAddDocument?.child]);

  const graphConcordanceHandlers = useU3141GraphConcordanceHandlers({
    masterLotChainId,
    t,
    toast,
    applyLotChainGraphSnapshot: u3141MasterChildLotChainHandlers.applyLotChainGraphSnapshot,
    enabled: hasRole(U3141ROLES.EDIT),
  });

  const handleOpenDocumentError = useCallback(
    ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(
        error as Parameters<typeof U2000HandleApiError>[0],
        t,
        NS,
        luHandleAppiError
      );
      if (errorResult.genericErrors?.length && document.visibilityState === "visible") {
        errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
      }
    },
    [t, toast]
  );

  const { openDocumentPdfFile } = useU3141OpenDocumentPdfFileHandlers({
    axios: u2000AxiosAuthenticated,
    onError: handleOpenDocumentError,
  });

  const graphDialogOpenRef = useRef(false);
  graphDialogOpenRef.current = u3141MasterChildLotChainHandlers.isReactFlowDialogOpen;

  const graphApiHandlers = useU3141LotChainGraphApiHandlers({
    t,
    toast,
    loadAndOpenLotChainGraph: u3141MasterChildLotChainHandlers.loadAndOpenLotChainGraph,
    getGridData: () => gridMasterChildLotChainStateHandlers.getData?.() ?? [],
    refetchMasterLotChain: (u3141MasterLotChainFormHandlers as { refetchMasterLotChain?: () => Promise<void> }).refetchMasterLotChain,
    refreshInitialGraphWithFullHierarchyLayout: u3141MasterChildLotChainHandlers.refreshInitialGraphWithFullHierarchyLayout,
    bumpGraphInitialLayoutKey: u3141MasterChildLotChainHandlers.bumpGraphInitialLayoutKey,
    isGraphDialogOpen: () => graphDialogOpenRef.current,
  });
  const loadLotChainGraphRef = useRef<(mid: string) => Promise<void>>(async () => {});
  const reloadLotChainGraphAfterStructureChangeRef = useRef<(mid: string) => Promise<void>>(async () => {});
  const refetchMasterLotChainRef = useRef<(() => Promise<void>) | undefined>(undefined);
  /** Évite deux séries GET maître + lotChainGraph si deux événements WS arrivent pendant le premier rafraîchissement. */
  const annotatedChainRefreshInFlightRef = useRef(false);
  loadLotChainGraphRef.current = graphApiHandlers.loadLotChainGraph;
  reloadLotChainGraphAfterStructureChangeRef.current = graphApiHandlers.reloadLotChainGraphAfterChainStructureChange;
  refetchMasterLotChainRef.current = (u3141MasterLotChainFormHandlers as { refetchMasterLotChain?: () => Promise<void> })
    .refetchMasterLotChain;

  const openLotChainGraphConsumedRef = useRef(false);

  useEffect(() => {
    openLotChainGraphConsumedRef.current = false;
  }, [masterLotChainId]);

  /** BL-0710 — ouverture auto du graphe après création registre depuis U3121. */
  useEffect(() => {
    const navState = (routerLocation.state ?? null) as U3141LandRegisterLocationState | null;
    if (!navState?.openLotChainGraph || openLotChainGraphConsumedRef.current) return;
    if (!masterLotChainId || !isValidId(masterLotChainId)) return;

    openLotChainGraphConsumedRef.current = true;
    void (async () => {
      await loadLotChainGraphRef.current(masterLotChainId);
      navigate(`${routerLocation.pathname}${routerLocation.search}`, {
        replace: true,
        state: {
          ...navState,
          openLotChainGraph: false,
        },
      });
    })();
  }, [masterLotChainId, navigate, routerLocation.pathname, routerLocation.search, routerLocation.state]);

  const refreshMasterAndOpenGraph = useCallback(
    async (mid: string, options?: { relayoutStructure?: boolean }) => {
      if (annotatedChainRefreshInFlightRef.current) {
        if (import.meta.env.DEV) {
          console.log("[U3141] Rafraîchissement graphe déjà en cours, ignoré.");
        }
        return;
      }
      annotatedChainRefreshInFlightRef.current = true;
      try {
        await refetchMasterLotChainRef.current?.();
        if (graphDialogOpenRef.current) {
          if (options?.relayoutStructure) {
            await reloadLotChainGraphAfterStructureChangeRef.current(mid);
          } else {
            await loadLotChainGraphRef.current(mid);
          }
        }
      } finally {
        annotatedChainRefreshInFlightRef.current = false;
      }
    },
    [],
  );

  useEffect(() => {
    const extended = u3141MasterLotChainFormHandlers as {
      refreshLotChainGraphIfOpen?: (options?: { relayoutStructure?: boolean }) => Promise<void>;
      beforeAnnotatedMergeJobLaunch?: (anchorChildLotChainId: string) => void;
    };
    extended.refreshLotChainGraphIfOpen = async (options?: { relayoutStructure?: boolean }) => {
      if (!masterLotChainId || !graphDialogOpenRef.current) return;
      if (options?.relayoutStructure) {
        await reloadLotChainGraphAfterStructureChangeRef.current(masterLotChainId);
      } else {
        await loadLotChainGraphRef.current(masterLotChainId);
      }
    };
    extended.beforeAnnotatedMergeJobLaunch = (anchorChildLotChainId: string) => {
      u3141MasterChildLotChainHandlers.removeAnnotatedLotChainDocumentFromGraphNode(anchorChildLotChainId);
    };
    return () => {
      delete extended.refreshLotChainGraphIfOpen;
      delete extended.beforeAnnotatedMergeJobLaunch;
    };
  }, [masterLotChainId, u3141MasterLotChainFormHandlers, u3141MasterChildLotChainHandlers]);

  const deleteFromGraphHandlers = useU3141DeleteChildLotChainFromGraphHandlers();

  const handlePdfAnnotationSaved = useCallback(async () => {
    await refetchMasterLotChainRef.current?.();
    if (graphDialogOpenRef.current && masterLotChainId) {
      await loadLotChainGraphRef.current(masterLotChainId);
    }
  }, [masterLotChainId]);

  useU3141AnnotatedLotChainNotification(masterLotChainId || undefined, async (payload) => {
    if (payload.failed) return;
    await refreshMasterAndOpenGraph(payload.masterLotChainId);
  });

  /** Après import PDF chaîne (BL-0562 / BL-0707) : relayout hiérarchique si graphe ouvert. */
  useU3141ReconstituteChainFromPdfNotification(masterLotChainId || undefined, async (payload) => {
    await refreshMasterAndOpenGraph(payload.masterLotChainId, { relayoutStructure: true });
  });

  /** Après job historique cadastral (BL-0709) : relayout structure comme reconstitute PDF. */
  useU3141HistoriqueCadastralNotification(masterLotChainId || undefined, async (mid) => {
    await refreshMasterAndOpenGraph(mid, { relayoutStructure: true });
  });

  /** Après import annotations seules (BL-0700) : même rafraîchissement que reconstituteChainFromPdf. */
  useU3141ImportChainAnnotationsNotification(masterLotChainId || undefined, async (payload) => {
    if (payload.failed) return;
    await refreshMasterAndOpenGraph(payload.masterLotChainId);
  });

  const handleGraphDeleteConfirmClose = useCallback(
    async (result: "yes" | "no") => {
      if (result === "yes" && pendingGraphDeleteNode && masterLotChainId) {
        const clc = pendingGraphDeleteNode.data?.nodePayload as U3141ChildLotChain | undefined;
        if (clc?.id) {
          const deleteResult = await deleteFromGraphHandlers.deleteChildLotChainFromGraph(masterLotChainId, clc);
          if (deleteResult.ok) {
            const graph = deleteResult.lotChainGraph;
            if (graph) {
              u3141MasterChildLotChainHandlers.applyLotChainGraphSnapshot(graph);
              const synced = u3141SyncChildLotChainGridFromLotChainGraph(
                graph,
                gridMasterChildLotChainStateHandlers.getData(),
              );
              gridMasterChildLotChainStateHandlers.setData(synced);
              gridMasterChildLotChainStateHandlers.setTotalRows(synced.length);
              u3141MasterChildLotChainHandlers.setDataChangedEvent(
                u3141MasterChildLotChainHandlers.dataChangedEvent + 1,
              );
            } else {
              await refetchMasterLotChainRef.current?.();
            }
            const lotLabel = clc.lotDetail?.lot?.trim() || clc.id;
            toast.success(t("msgGraphRemoveLotFromChainSuccess", { lot: lotLabel, ns: NS }), t("successTitle", { ns: NS }));
          }
        }
      }
      setGraphDeleteConfirmOpen(false);
      setPendingGraphDeleteNode(null);
    },
    [
      pendingGraphDeleteNode,
      masterLotChainId,
      deleteFromGraphHandlers,
      u3141MasterChildLotChainHandlers,
      gridMasterChildLotChainStateHandlers,
      t,
      toast,
    ],
  );

  const requestedLot = u3141MasterLotChainFormHandlers.getData?.()?.requestedLot ?? "";

  const openDocumentInNewTab = useCallback(
    async (documentId: string, documentType?: string) => {
      if (u3141IsNasTruthDocumentType(documentType)) {
        await openDocumentPdfFile(documentId);
        return;
      }
      // Chaîne annotée et autres : signUrl S3 (BL-0473 / hors NAS).
      const signUrl = await graphApiHandlers.getDocumentSignUrl(documentId);
      if (signUrl) {
        window.open(signUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error(t("errorNoDocumentUrl", { ns: NS }), t("errorTitle", { ns: NS }));
      }
    },
    [t, toast, graphApiHandlers, openDocumentPdfFile]
  );

  const openGraphImportDocument = useCallback((child: U3141ChildLotChain, initialDocumentType: string) => {
    const existingDocuments = u3141MapDocumentTypeEntriesToExistingDocuments(child.lotDetail?.documentTypes);
    setGraphAddDocument({ child, initialDocumentType, existingDocuments });
  }, []);

  const handleGraphDocumentsChanged = useCallback(
    async (detail?: U3141ChildLotChainDocumentsChangeDetail) => {
      await u3141MasterLotChainFormHandlers.refreshLotChainGraphIfOpen?.();
      if (detail?.childLotChainId && detail.documents) {
        u3141MasterChildLotChainHandlers.patchGraphNodeDocumentTypesFromDocumentsList(
          detail.childLotChainId,
          detail.documents,
        );
        setGraphManageDocumentsChild((prev) => {
          if (!prev || String(prev.id) !== String(detail.childLotChainId)) {
            return prev;
          }
          return u3141SyncChildLotChainDocumentTypesFromDocumentsList(prev, detail.documents);
        });
        const gridRows = gridMasterChildLotChainStateHandlers.getData?.() ?? [];
        const nextGrid = gridRows.map((row) =>
          String(row.id) === String(detail.childLotChainId)
            ? u3141SyncChildLotChainDocumentTypesFromDocumentsList(row, detail.documents)
            : row,
        );
        gridMasterChildLotChainStateHandlers.setData(nextGrid);
      }
      await u3141MasterLotChainFormHandlers.refetchMasterLotChain?.();
    },
    [
      u3141MasterLotChainFormHandlers,
      u3141MasterChildLotChainHandlers,
      gridMasterChildLotChainStateHandlers,
    ],
  );

  const getContextMenuItems = useCallback(
    (node: Node): U2000ReactFlowContextMenuItem[] => {
      const items: U2000ReactFlowContextMenuItem[] = [];
      const childLotChain = node.data?.nodePayload as U3141ChildLotChain | undefined;
      const isRequestedLot = u3141IsRequestedLotChildLotChain(childLotChain, requestedLot);
      const historiqueSectionItems: U2000ReactFlowContextMenuItem[] = [];
      if (isRequestedLot && childLotChain) {
        const masterData = u3141MasterLotChainFormHandlers.getData?.();
        const formState = u3141MasterLotChainFormHandlers.getFormState?.() ?? U2000FormStateEnumState.VIEW;
        const hasHistoriqueCadastralSauvegarde = Boolean(masterData?.hasHistoriqueCadastralSauvegarde);
        const historiqueActions = u3141MasterLotChainHistoriqueActionState({
          formState,
          masterData,
          fetchHistoriqueCadastralCooldown: Boolean(u3141MasterLotChainFormHandlers.fetchHistoriqueCadastralCooldown),
          hasRoleRegfon: hasRole(U3141ROLES.REGFON),
          hasRoleEdit: hasRole(U3141ROLES.EDIT),
          openReconstituteChainFromPdfAvailable: Boolean(u3141MasterLotChainFormHandlers.openReconstituteChainFromPdf),
        });
        if (historiqueActions.canShowFetchHistoriqueCadastral) {
          historiqueSectionItems.push({
            key: "ctx-fetch-historique",
            label: t("ctxMenuFetchHistoriqueCadastral", { ns: NS }),
            icon: FaHistory,
            itemVariant: "action",
            disabled: historiqueActions.fetchHistoriqueCadastralDisabled,
            onClick: () => {
              if (masterLotChainId) {
                void u3141MasterLotChainFormHandlers.fetchHistoriqueCadastral(masterLotChainId);
              }
            },
          });
        }
        if (hasRole(U3141ROLES.EDIT)) {
          historiqueSectionItems.push({
            key: "ctx-download-historique-html",
            label: t("btnDownloadHistoriqueCadastralHtml", { ns: NS }),
            icon: FaFileDownload,
            itemVariant: "action",
            disabled:
              formState === U2000FormStateEnumState.EDIT || !masterLotChainId || !hasHistoriqueCadastralSauvegarde,
            onClick: () => {
              if (masterLotChainId) {
                void u3141MasterLotChainFormHandlers.downloadHistoriqueCadastralHtml(masterLotChainId);
              }
            },
          });
        }
        if (historiqueActions.canShowImportChainFromPdf) {
          historiqueSectionItems.push({
            key: "ctx-import-chain-pdf",
            label: t("ctxMenuImportChainFromPdf", { ns: NS }),
            icon: FaFileUpload,
            itemVariant: "action",
            disabled: historiqueActions.importChainFromPdfDisabled,
            onClick: () => u3141MasterLotChainFormHandlers.openReconstituteChainFromPdf?.(),
          });
        }
      }
      u3141AppendGraphContextMenuSection(
        items,
        "section-historique-cadastral",
        t("ctxMenuSectionHistoriqueCadastral", { ns: NS }),
        historiqueSectionItems,
      );

      const documentTypes = childLotChain?.lotDetail?.documentTypes;
      const withIdRaw = Array.isArray(documentTypes) ? documentTypes.filter((e) => e?.id) : [];
      const withId = [...withIdRaw].sort(u3141CompareDocumentEntriesForGraphContextMenu);

      const annotatedChainSectionItems: U2000ReactFlowContextMenuItem[] = [];
      const annotatedChainPdfItems: U2000ReactFlowContextMenuItem[] = [];
      const annotatedChainImportItems: U2000ReactFlowContextMenuItem[] = [];
      if (childLotChain && hasRole(U3141ROLES.READ)) {
        annotatedChainSectionItems.push({
          key: "annotated-lot-chain",
          label: t("ctxMenuAnnotatedLotChain", { ns: NS }),
          icon: FaProjectDiagram,
          itemVariant: "action",
          onClick: () => {
            const childId = childLotChain?.id;
            if (childId) {
              u3141MasterLotChainFormHandlers.openGenerateAnnotatedLotChain?.(childId);
            }
          },
        });
      }
      const annotatedChainDocEntry = withId.find((e) => e.documentType === "rfLotDetailAnnotatedLotChain");
      withId.forEach((e) => {
        if (e.documentType !== "rfLotDetailAnnotatedLotChain" || !childLotChain) {
          return;
        }
        const typeLabel = t(`lblDocumentType.${e.documentType}`, { ns: NS });
        const labelResolved = typeLabel !== `lblDocumentType.${e.documentType}` ? typeLabel : e.documentType;
        const docLabel = u3141GraphContextMenuDocumentLabel(labelResolved, e.documentName);
        annotatedChainPdfItems.push({
          key: `doc-${e.id}`,
          label: docLabel.label,
          title: docLabel.title,
          icon: FaRegFileAlt,
          itemVariant: "document",
          onClick: () => openDocumentInNewTab(e.id, e.documentType),
        });
      });
      const lotDocumentTypes = childLotChain?.lotDetail?.documentTypes;
      const showImportChainAnnotations =
        u3141LotHasAnnotatedLotChainDocument(lotDocumentTypes) &&
        hasRole(U3141ROLES.REGFON) &&
        Boolean(u3141MasterLotChainFormHandlers.openImportChainAnnotations);
      if (showImportChainAnnotations) {
        const importAnnotationsMenuKey = annotatedChainDocEntry?.id
          ? `doc-import-annotations-${annotatedChainDocEntry.id}`
          : "doc-import-annotations";
        annotatedChainImportItems.push({
          key: importAnnotationsMenuKey,
          label: t("ctxMenuImportChainAnnotations", { ns: NS }),
          icon: FaFileImport,
          itemVariant: "action",
          disabled: !masterLotChainId,
          onClick: () => u3141MasterLotChainFormHandlers.openImportChainAnnotations?.(),
        });
      }
      annotatedChainSectionItems.push(...annotatedChainImportItems);
      u3141AppendGraphContextMenuSection(
        items,
        "section-annotated-lot-chain",
        t("ctxMenuSectionAnnotatedLotChain", { ns: NS }),
        annotatedChainSectionItems,
      );

      const documentBlockItems: U2000ReactFlowContextMenuItem[] = [...annotatedChainPdfItems];
      const withIdExcludingAnnotatedChain = withId.filter((e) => e.documentType !== "rfLotDetailAnnotatedLotChain");
      if (withIdExcludingAnnotatedChain.length === 0 && annotatedChainPdfItems.length === 0) {
        const showManageDocuments = Boolean(childLotChain && hasRole(U3141ROLES.READ));
        if (!showManageDocuments) {
          documentBlockItems.push({ key: "no-doc", label: t("msgNoDocuments", { ns: NS }), disabled: true });
        }
      } else {
        withIdExcludingAnnotatedChain.forEach((e) => {
          const typeLabel = t(`lblDocumentType.${e.documentType}`, { ns: NS });
          const labelResolved = typeLabel !== `lblDocumentType.${e.documentType}` ? typeLabel : e.documentType;
          const docLabel = u3141GraphContextMenuDocumentLabel(labelResolved, e.documentName);
          documentBlockItems.push({
            key: `doc-${e.id}`,
            label: docLabel.label,
            title: docLabel.title,
            icon: FaRegFileAlt,
            itemVariant: "document",
            onClick: () => openDocumentInNewTab(e.id, e.documentType),
          });
        });
      }
      if (childLotChain && hasRole(U3141ROLES.ADD)) {
        const existingForImport = u3141MapDocumentTypeEntriesToExistingDocuments(childLotChain.lotDetail?.documentTypes);
        const addableFromInit =
          unitInitLU.addableDocumentTypes?.length > 0
            ? unitInitLU.addableDocumentTypes
            : [...U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG];
        const singleSlotTypes =
          unitInitLU.singleSlotDocumentTypes?.length > 0 ? unitInitLU.singleSlotDocumentTypes : ["rfLotDetailIndex"];
        const importMenuTypes = u3141ResolveImportDocumentMenuTypes(
          addableFromInit,
          existingForImport,
          singleSlotTypes,
        );
        if (importMenuTypes.length === 1) {
          const onlyType = importMenuTypes[0];
          documentBlockItems.push({
            key: "ctx-import-document",
            label: t("ctxMenuImportDocument", { ns: NS }),
            icon: FaFileUpload,
            itemVariant: "action",
            onClick: () => openGraphImportDocument(childLotChain, onlyType),
          });
        } else if (importMenuTypes.length > 1) {
          documentBlockItems.push({
            key: "ctx-import-document",
            label: t("ctxMenuImportDocument", { ns: NS }),
            icon: FaFileUpload,
            itemVariant: "action",
            subItems: importMenuTypes.map((docType) => {
              const typeLabel = t(`lblDocumentType.${docType}`, { ns: NS });
              const labelResolved = typeLabel !== `lblDocumentType.${docType}` ? typeLabel : docType;
              return {
                key: `ctx-import-doc-${docType}`,
                label: labelResolved,
                itemVariant: "action" as const,
                onClick: () => openGraphImportDocument(childLotChain, docType),
              };
            }),
          });
        }
      }
      if (childLotChain && hasRole(U3141ROLES.READ)) {
        documentBlockItems.push({
          key: "ctx-manage-documents",
          label: t("ctxMenuManageDocuments", { ns: NS }),
          icon: FaFolderOpen,
          itemVariant: "dialog",
          onClick: () => setGraphManageDocumentsChild(childLotChain),
        });
      }
      u3141AppendGraphContextMenuSection(
        items,
        "section-documents",
        t("ctxMenuSectionDocuments", { ns: NS }),
        documentBlockItems,
      );

      const lotEditDeleteItems: U2000ReactFlowContextMenuItem[] = [];
      if (hasRole(U3141ROLES.EDIT) && childLotChain) {
        lotEditDeleteItems.push({
          key: "edit-lot",
          label: t("ctxMenuEditLot", { ns: NS }),
          icon: FaPen,
          itemVariant: "action",
          onClick: () => {
            setEditLotFromGraphChild(childLotChain);
            setEditLotFromGraphOpen(true);
          },
        });
      }
      if (
        hasRole(U3141ROLES.DELETE) &&
        childLotChain &&
        u3141IsGraphDeletableChildLotChain(childLotChain, requestedLot)
      ) {
        lotEditDeleteItems.push({
          key: "delete-graph-lot",
          label: t("ctxMenuDeleteOrphanLot", { ns: NS }),
          icon: FaTrashAlt,
          itemVariant: "destructive",
          onClick: () => {
            setPendingGraphDeleteNode(node);
            setGraphDeleteConfirmOpen(true);
          },
        });
      }
      u3141AppendGraphContextMenuSection(
        items,
        "section-lot-actions",
        t("ctxMenuSectionLot", { ns: NS }),
        lotEditDeleteItems,
      );
      return items;
    },
    [
      t,
      openDocumentInNewTab,
      hasRole,
      requestedLot,
      u3141MasterLotChainFormHandlers,
      masterLotChainId,
      unitInitLU.addableDocumentTypes,
      unitInitLU.singleSlotDocumentTypes,
      openGraphImportDocument,
    ]
  );

  const getEdgeContextMenuItems = useCallback(
    (edge: Edge): U2000ReactFlowEdgeContextMenuItem[] => {
      if (!hasRole(U3141ROLES.EDIT) || !graphConcordanceHandlers.nodesConnectable) {
        return [];
      }
      const meta = (edge.data as U3141GraphConcordanceEdgeData | undefined)?.u3141GraphConcordance;
      if (!meta?.concordanceHolderChildLotChainId || !meta.concordanceOriginChildLotChainId) {
        return [];
      }
      const items: U2000ReactFlowEdgeContextMenuItem[] = [];
      u3141AppendGraphContextMenuSection(
        items as U2000ReactFlowContextMenuItem[],
        "section-graph-edge",
        t("ctxMenuSectionGraphEdge", { ns: NS }),
        [
          {
            key: "unlink-graph-edge",
            label: t("ctxMenuDeleteGraphEdge", { ns: NS }),
            icon: FaUnlink,
            itemVariant: "destructive",
            onClick: (selectedEdge) => {
              void graphConcordanceHandlers.onEdgesDelete([selectedEdge as Edge]);
            },
          },
        ] as U2000ReactFlowContextMenuItem[],
      );
      return items;
    },
    [t, hasRole, graphConcordanceHandlers],
  );

  const onNodeVisibleChange = useCallback(
    (params: { masterLotChainId: string; childLotChainIds: string[]; visible: boolean }) =>
      graphApiHandlers.onNodeVisibleChange(params),
    [graphApiHandlers]
  );

  const onSavePositions = useCallback(
    async (positions: U3141NodePositions): Promise<boolean> => {
      if (!masterLotChainId) return false;
      return graphApiHandlers.saveNodePositions(masterLotChainId, positions);
    },
    [masterLotChainId, graphApiHandlers]
  );

  const graphUnsavedPositionsCloseLabels = useMemo(
    () => ({
      title: t('dlgGraphCloseUnsavedPositionsTitle', { ns: NS }),
      message: t('dlgGraphCloseUnsavedPositionsMessage', { ns: NS }),
      saveAndExit: t('btnGraphCloseSaveAndExit', { ns: NS }),
      discard: t('btnGraphCloseDiscard', { ns: NS }),
      cancel: t('btnGraphCloseCancel', { ns: NS }),
    }),
    [t],
  );

  const suspendGraphCloseGuard =
    isGraphDeleteConfirmOpen ||
    isAddLotFromGraphOpen ||
    isEditLotFromGraphOpen ||
    graphManageDocumentsChild !== null ||
    graphAddDocument !== null ||
    pdfAnnotationEditor !== null;

  const onResetLayout = useCallback(async () => {
    if (!masterLotChainId) return;
    await graphApiHandlers.resetNodePositions(masterLotChainId);
  }, [masterLotChainId, graphApiHandlers]);

  const onPaneContextMenuOpen = useCallback((detail: U2000ReactFlowPaneContextOpenDetail) => {
    setAddLotNodePosition(detail.flowPosition);
  }, []);

  const paneContextMenuItems = useMemo((): U2000ReactFlowPaneContextMenuItem[] => {
    if (!hasRole(U3141ROLES.ADD)) return [];
    return [
      {
        key: "addLot",
        label: t("paneMenu.addLot", { ns: NS }),
        icon: FaPlus,
        itemVariant: "action",
        onClick: () => setAddLotFromGraphOpen(true),
      },
    ];
  }, [t, hasRole]);

  const handleGraphChildCreated = useCallback(
    (child: U3141ChildLotChain) => {
      const grid = gridMasterChildLotChainStateHandlers.getData?.() ?? [];
      const exists = grid.some((row) => row.id === child.id);
      if (exists) {
        gridMasterChildLotChainStateHandlers.setDataById(child.id, child);
      } else {
        gridMasterChildLotChainStateHandlers.setData([...grid, child]);
        gridMasterChildLotChainStateHandlers.setTotalRows(grid.length + 1);
      }
    },
    [gridMasterChildLotChainStateHandlers],
  );

  const handleGraphChildUpdated = useCallback(
    (child: U3141ChildLotChain) => {
      gridMasterChildLotChainStateHandlers.setDataById(child.id, child);
    },
    [gridMasterChildLotChainStateHandlers],
  );

  const handleViewLotchain = useCallback(async () => {
    if (!masterLotChainId) return;
    setLotChainGraphLoading(true);
    try {
      await graphApiHandlers.loadLotChainGraph(masterLotChainId);
    } finally {
      setLotChainGraphLoading(false);
    }
  }, [masterLotChainId, graphApiHandlers]);

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "view",
        label: t("btnModify", { ns: NS }),
        handleClick: u3141MasterChildLotChainHandlers.onModifyChildLotChain,
        hasRole: hasRole(U3141ROLES.EDIT),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "viewLotChain",
        label: t("btnMasterLotchain.viewLotChain", { ns: NS }),
        handleClick: handleViewLotchain,
        hasRole: hasRole(U3141ROLES.READ),
        disabled: disableViewBtn() || isLotChainGraphLoading,
        icon: FaEye,
        Component: U2000ActionBtn,
      },
    ],
    [t, u3141MasterChildLotChainHandlers, handleViewLotchain, isLotChainGraphLoading]
  );

  // Render
  return (
    <>
      <U31414MasterChildLotChainDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <U2000YesNoDialog
        isOpen={isGraphDeleteConfirmOpen}
        onClose={handleGraphDeleteConfirmClose}
        title={t("dlgDeleteOrphanLotTitle", { ns: NS })}
        overlayClassName="z-100"
      >
        <p className="mb-4">
          {(() => {
            const pendingClc = pendingGraphDeleteNode?.data?.nodePayload as U3141ChildLotChain | undefined;
            const isOrphan =
              pendingClc != null && u3141IsOrphanChildLotChain(pendingClc, requestedLot);
            return t(isOrphan ? "dlgDeleteOrphanLotMessage" : "dlgDeleteSubtreeLotMessage", { ns: NS });
          })()}
        </p>
        {pendingGraphDeleteNode && (
          <div className="mt-2 flex flex-wrap justify-center gap-x-2">
            <span className="inline-block text-gray-800">
              {(pendingGraphDeleteNode.data?.nodePayload as U3141ChildLotChain | undefined)?.lotDetail?.lot ?? ""}
            </span>
          </div>
        )}
      </U2000YesNoDialog>

      <U2000ReactFlowDialog
        open={u3141MasterChildLotChainHandlers.isReactFlowDialogOpen}
        initialNodes={u3141MasterChildLotChainHandlers.initialNodes}
        initialEdges={u3141MasterChildLotChainHandlers.initialEdges}
        onClose={() => u3141MasterChildLotChainHandlers.setReactFlowDialogOpen(false)}
        unsavedPositionsCloseLabels={graphUnsavedPositionsCloseLabels}
        suspendCloseGuard={suspendGraphCloseGuard}
        title={t("dlgMasterLotchain.viewLotChain", { ns: NS })}
        getContextMenuItems={getContextMenuItems}
        getEdgeContextMenuItems={getEdgeContextMenuItems}
        paneContextMenuItems={paneContextMenuItems}
        onPaneContextMenuOpen={onPaneContextMenuOpen}
        showCheckbox={true}
        checkboxDisabled={!hasRole(U3141ROLES.EDIT)}
        masterLotChainId={masterLotChainId}
        onNodeVisibleChange={onNodeVisibleChange}
        onSavePositions={onSavePositions}
        onResetLayout={onResetLayout}
        savePositionsLabel={t("paneMenu.savePositions", { ns: NS })}
        resetLayoutLabel={t("paneMenu.resetLayout", { ns: NS })}
        paneMenuSectionAddLabel={t("paneMenu.sectionLot", { ns: NS })}
        paneMenuSectionLayoutLabel={t("paneMenu.sectionLayout", { ns: NS })}
        onConnect={graphConcordanceHandlers.onConnect}
        isValidConnection={graphConcordanceHandlers.isValidConnection}
        nodesConnectable={graphConcordanceHandlers.nodesConnectable}
        edgesDeletable={false}
        initialGraphLayoutKey={u3141MasterChildLotChainHandlers.graphInitialLayoutKey}
        liveGraphNodesRef={u3141MasterChildLotChainHandlers.graphLiveNodesRef}
        positionsBaselineCommitKey={u3141MasterChildLotChainHandlers.graphPositionsBaselineCommitKey}
      />

      <U3141AddLotFromGraphDialog
        isOpen={isAddLotFromGraphOpen}
        onClose={() => setAddLotFromGraphOpen(false)}
        masterLotChainId={masterLotChainId}
        nodePosition={addLotNodePosition}
        existingChildLotChains={gridMasterChildLotChainStateHandlers.getData?.() ?? []}
        applyLotChainGraphSnapshotFromGraphCreate={
          u3141MasterChildLotChainHandlers.applyLotChainGraphSnapshotFromGraphCreate
        }
        refetchMasterLotChain={
          (u3141MasterLotChainFormHandlers as { refetchMasterLotChain?: () => Promise<void> }).refetchMasterLotChain
        }
        reloadLotChainGraphIfDialogOpen={async (mid) => {
          if (!u3141MasterChildLotChainHandlers.isReactFlowDialogOpen) {
            return;
          }
          await loadLotChainGraphRef.current(mid);
          u3141MasterChildLotChainHandlers.bumpGraphPositionsBaselineCommitKey();
        }}
        onChildCreated={handleGraphChildCreated}
      />

      <U3141EditLotFromGraphDialog
        isOpen={isEditLotFromGraphOpen}
        onClose={() => {
          setEditLotFromGraphOpen(false);
          setEditLotFromGraphChild(null);
        }}
        masterLotChainId={masterLotChainId}
        initialChild={editLotFromGraphChild}
        onChildUpdated={handleGraphChildUpdated}
        patchGraphNode={u3141MasterChildLotChainHandlers.patchGraphNodeFromChildLotChain}
        refetchMasterLotChain={
          (u3141MasterLotChainFormHandlers as { refetchMasterLotChain?: () => Promise<void> }).refetchMasterLotChain
        }
      />

      <U3141GraphChildLotDocumentsDialog
        open={graphManageDocumentsChild !== null}
        onClose={() => setGraphManageDocumentsChild(null)}
        masterLotChainId={masterLotChainId}
        childLotChain={graphManageDocumentsChild}
        onDocumentsChanged={handleGraphDocumentsChanged}
      />

      {graphAddDocument?.child.id ? (
        <U3141AddDocumentDialog
          isOpen
          onClose={() => setGraphAddDocument(null)}
          onSuccess={() => {
            const childId = graphAddDocument.child.id;
            void loadGraphAddDocuments().then((list) => {
              void handleGraphDocumentsChanged({ childLotChainId: childId, documents: list });
              setGraphAddDocument(null);
            });
          }}
          masterLotChainId={masterLotChainId}
          childLotChainId={graphAddDocument.child.id}
          currentProject={graphAddDocumentCurrentProject}
          existingDocuments={graphAddDocument.existingDocuments}
          addableDocumentTypes={graphAddDocumentAddableTypes}
          singleSlotDocumentTypes={graphAddDocumentSingleSlotTypes}
          initialDocumentType={graphAddDocument.initialDocumentType}
          overlayClassName="z-110"
        />
      ) : null}

      <U3141PdfAnnotationEditorDialog
        isOpen={pdfAnnotationEditor !== null}
        documentId={pdfAnnotationEditor?.documentId ?? null}
        concordanceParentContext={
          pdfAnnotationEditor
            ? {
                codeDistrict: pdfAnnotationEditor.childLotChain.lotDetail?.district?.codeDistrict,
                cadastreCode: pdfAnnotationEditor.childLotChain.lotDetail?.cadastre?.cadastreCode,
              }
            : null
        }
        onClose={() => setPdfAnnotationEditor(null)}
        onSaved={handlePdfAnnotationSaved}
        overlayClassName="z-120"
      />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridMasterChildLotChainStateHandlers}
        rowClickFn={u3141MasterChildLotChainHandlers.onRowClick as unknown as (row: any) => void}
        allowRowSelection={u3141MasterLotChainFormHandlers.getFormState?.() !== U2000FormStateEnumState.EDIT}
        label={t("lblChildLotChain", {
          count: u3141MasterChildLotChainHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U3141MasterChildLotChainBody;
